import hljs from "highlight.js/lib/core";
import ReactMarkdown from "react-markdown";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomOneLight } from "react-syntax-highlighter/dist/esm/styles/hljs";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
}) => {
  return (
    <div className={cn("prose prose-sm max-w-none markdown text-[16px] leading-[1.5]", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          a: (props) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
            />
          ),
          p: (props => <div {...props} />),
          pre: (props) => <pre {...props} className="bg-[#0000] p-0" />,
          code: ({ children, ...props }) => {
            const codeString = String(children).replace(/\n$/, "");

            // 改行を含む場合はコードブロック、含まない場合はインラインコード
            const isCodeBlock = codeString.includes("\n");

            // インラインコードの場合はシンプルに描画
            if (!isCodeBlock) {
              return (
                <code
                  {...props}
                  className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono"
                >
                  {children}
                </code>
              );
            }

            // コードブロックの場合はSyntaxHighlighterを使用
            const { language } = hljs.highlightAuto(codeString);

            return (
              <SyntaxHighlighter
                language={language}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore-next-line
                style={atomOneLight}
                PreTag="div"
                wrapLongLines
                customStyle={{
                  padding: "16px",
                  borderRadius: 6,
                  fontSize: 16,
                }}
                {...props}
              >
                {codeString}
              </SyntaxHighlighter>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
